/** The scrubber's arithmetic (docs/SPEC.md §7.4, §7.5, §8.2 `span`): the
 *  scenes on the film's clock, the stretch the track shows (the film, or the
 *  focused scene alone), a part of a scene marked by a drag, and where a
 *  dragged scene lands. No DOM, no three. */
import type { Span } from "../api/changes";
import type { Manifest } from "../film/manifest";
import { segments } from "../film/play";

/** One scene as the scrubber shows it. */
export interface ScrubScene {
  id: string;
  name: string;
  seconds: number;
  /** Film time of its local 0, and of its end. */
  start: number;
  end: number;
  locked: boolean;
  /** Built and playable (§6.1): while the film builds, a scene not built
   *  yet is a grey tile that cannot be picked. */
  ready: boolean;
}

/** The manifest's scenes on its clock, with their locks. */
export function scrubScenes(
  manifest: Manifest,
  locked: (sceneId: string) => boolean,
): ScrubScene[] {
  return segments(manifest).map((seg) => ({
    id: seg.id,
    name: manifest.scenes[seg.index].name,
    seconds: seg.seconds,
    start: seg.start,
    end: seg.end,
    locked: locked(seg.id),
    ready: manifest.scenes[seg.index].ready,
  }));
}

/** The film's length: where its last scene ends. */
export function lengthOf(scenes: readonly ScrubScene[]): number {
  return scenes.length ? scenes[scenes.length - 1].end : 0;
}

const clamp = (x: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, Number.isFinite(x) ? x : lo));
const tenth = (x: number) => Math.round(x * 10) / 10 + 0;

/** The film-time stretch the track shows: the whole film, or the focused
 *  scene (§7.5: "the scrubber shows that scene's moments"). */
export function trackRange(
  scenes: readonly ScrubScene[],
  focus: string | null,
): [number, number] {
  const scene = focus ? scenes.find((s) => s.id === focus) : undefined;
  if (scene) return [scene.start, scene.start + scene.seconds];
  return [0, lengthOf(scenes)];
}

/** Film time at a share (0..1) of the track. */
export function timeAt(fraction: number, range: [number, number]): number {
  return range[0] + clamp(fraction, 0, 1) * (range[1] - range[0]);
}

/** The share of the track at film time `t`. */
export function fractionOf(t: number, range: [number, number]): number {
  const span = range[1] - range[0];
  return span > 0 ? clamp((t - range[0]) / span, 0, 1) : 0;
}

/** The scene a film time belongs to (the later one through an overlap). */
export function sceneAt(
  scenes: readonly ScrubScene[],
  t: number,
): ScrubScene | null {
  let found: ScrubScene | null = null;
  for (const scene of scenes) if (t >= scene.start - 1e-9) found = scene;
  return found ?? scenes[0] ?? null;
}

/** The shortest part worth marking, seconds. */
export const SPAN_MIN = 0.2;

/** A part of one scene between two film times of a drag (§8.2 `span`):
 *  inside the scene where the drag began (the focused one when focused),
 *  local seconds to a tenth; null when it is shorter than `SPAN_MIN`. */
export function spanBetween(
  scenes: readonly ScrubScene[],
  focus: string | null,
  a: number,
  b: number,
): Span | null {
  const scene =
    (focus ? scenes.find((s) => s.id === focus) : null) ?? sceneAt(scenes, a);
  if (!scene) return null;
  const local = (t: number) => clamp(t - scene.start, 0, scene.seconds);
  const [t0, t1] = [local(a), local(b)].sort((p, q) => p - q);
  if (t1 - t0 < SPAN_MIN) return null;
  return { scene: scene.id, t0: tenth(t0), t1: tenth(t1) };
}

/** Where a scene dragged from `from` and dropped on the slot `to` (the
 *  index of the scene it is dropped on) lands: before which scene, `null`
 *  for the end; undefined when it does not move. Dropping on a later scene
 *  puts it after that scene, on an earlier one before it. */
export function moveTarget(
  scenes: readonly ScrubScene[],
  from: number,
  to: number,
): { before: string | null } | undefined {
  if (from === to || from < 0 || to < 0 || to >= scenes.length)
    return undefined;
  if (to < from) return { before: scenes[to].id };
  const after = scenes[to + 1];
  return { before: after ? after.id : null };
}

/** The index of the segment under `x` among segments' `[left, right]`
 *  (clamped to the first and the last). */
export function slotAt(bounds: readonly [number, number][], x: number): number {
  if (bounds.length === 0) return -1;
  for (let i = 0; i < bounds.length; i++) {
    if (x < bounds[i][1]) return i;
  }
  return bounds.length - 1;
}
